import { PDFDocument } from "pdf-lib";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";

import { APP_VERSION } from "../appVersion";
import { formatAllocationNumber } from "../utils/allocationNumber";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { saveWithdrawalDocumentDiagnostic, type WithdrawalDocumentDiagnostic, type WithdrawalDocumentOverlayMetrics } from "./withdrawalDocumentDiagnosticsService";
import { WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION } from "./withdrawalDocumentConstants";
export { WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION } from "./withdrawalDocumentConstants";

GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

export interface WithdrawalDocumentData {
  withdrawalDate: string;
  materialNumber: string;
  materialText: string;
  materialType: string;
  storageAddress: string;
  quantityRemoved: string;
  currentStock: string;
  persons: string[];
  accountingNumber: string;
  accountingDescription: string;
  orderInfo: string;
}

export interface WithdrawalDocumentArtifacts {
  pdf: Blob;
  preview: Blob;
  telemetry: {
    canonicalPdfMs: number;
    pdfjsFlattenRenderMs: number;
    flattenedPdfEncodeMs: number;
    webpEncodeMs: number;
    totalMs: number;
    pdfBytes: number;
    previewBytes: number;
  };
}

const TEMPLATE_URL = "/materialentnahmeschein-template.png";
const PAGE_WIDTH_PT = 595.2;
const PAGE_HEIGHT_PT = 419.52;
const PREVIEW_RENDER_SCALE = 1.7;
const FLATTEN_RENDER_SCALE = 2.5;

let templateBytesPromise: Promise<Uint8Array> | null = null;

async function loadTemplateBytesUncached(): Promise<Uint8Array> {
  // Die Vorlage gehört zum App-Shell-Cache und bleibt damit auch offline verfügbar.
  // Sie wird nicht mehr im Browser auf einen Canvas gezeichnet. pdf-lib bettet die
  // Original-PNG-Bytes direkt in die PDF ein; dadurch kann ein Browserdecoder die
  // Vorlage nicht mehr still als leere/weiße Fläche rendern.
  const response = await fetch(TEMPLATE_URL, {
    cache: "no-store",
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error(`Materialentnahmeschein-Vorlage konnte nicht geladen werden (${response.status}).`);
  const blob = await response.blob();
  if (blob.size < 10_000) throw new Error("Materialentnahmeschein-Vorlage ist leer oder unvollständig.");
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (pngSignature.some((value, index) => bytes[index] !== value)) {
    throw new Error("Materialentnahmeschein-Vorlage besitzt kein gültiges PNG-Format.");
  }
  return bytes;
}

async function loadTemplateBytes(): Promise<Uint8Array> {
  if (!templateBytesPromise) {
    templateBytesPromise = loadTemplateBytesUncached().catch((error) => {
      templateBytesPromise = null;
      throw error;
    });
  }
  return templateBytesPromise;
}

function formatDate(value: string): string {
  const parts = value.split("-");
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : value;
}

function fontFor(ctx: CanvasRenderingContext2D, sizePx: number, weight = 400): void {
  ctx.font = `${weight} ${sizePx}px Arial, Helvetica, sans-serif`;
  ctx.fillStyle = "#000";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
}

function fitAndDrawPdfText(
  ctx: CanvasRenderingContext2D,
  value: string,
  scale: number,
  xPt: number,
  topPt: number,
  fontPt: number,
  maxWidthPt: number,
  minFontPt: number,
  weight = 400,
): void {
  const text = value.trim();
  if (!text) return;
  let sizePt = fontPt;
  while (sizePt > minFontPt) {
    fontFor(ctx, sizePt * scale, weight);
    if (ctx.measureText(text).width <= maxWidthPt * scale) break;
    sizePt -= 0.25;
  }
  fontFor(ctx, sizePt * scale, weight);
  ctx.fillText(text, xPt * scale, (topPt + sizePt / 2) * scale, maxWidthPt * scale);
}

function fitAndDrawPdfBoxText(
  ctx: CanvasRenderingContext2D,
  value: string,
  scale: number,
  xPt: number,
  topPt: number,
  widthPt: number,
  heightPt: number,
  maxFontPt: number,
  minFontPt: number,
  weight = 400,
  align: "left" | "center" = "left",
  verticalOffsetPt = 0,
): void {
  const text = value.trim();
  if (!text) return;
  const wrap = (fontPt: number): string[] => {
    fontFor(ctx, fontPt * scale, weight);
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let line = "";
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width <= widthPt * scale) { line = candidate; continue; }
      if (line) lines.push(line);
      if (ctx.measureText(word).width <= widthPt * scale) { line = word; continue; }
      let part = "";
      for (const char of word) {
        const next = part + char;
        if (part && ctx.measureText(next).width > widthPt * scale) { lines.push(part); part = char; }
        else part = next;
      }
      line = part;
    }
    if (line) lines.push(line);
    return lines;
  };

  let fontPt = maxFontPt;
  let lines = wrap(fontPt);
  while (fontPt > minFontPt) {
    const lineHeightPt = fontPt * 1.12;
    if (lines.length * lineHeightPt <= heightPt) break;
    fontPt -= 0.25;
    lines = wrap(fontPt);
  }
  const lineHeightPt = fontPt * 1.12;
  const maxLines = Math.max(1, Math.floor(heightPt / lineHeightPt));
  if (lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    const last = lines.length - 1;
    while (lines[last].length > 1 && ctx.measureText(`${lines[last]}…`).width > widthPt * scale) lines[last] = lines[last].slice(0, -1);
    lines[last] = `${lines[last]}…`;
  }

  ctx.save();
  ctx.beginPath();
  ctx.rect(xPt * scale, topPt * scale, widthPt * scale, heightPt * scale);
  ctx.clip();
  fontFor(ctx, fontPt * scale, weight);
  ctx.textAlign = align;
  const drawX = (align === "center" ? xPt + widthPt / 2 : xPt) * scale;
  const totalHeight = lines.length * lineHeightPt;
  const startY = topPt + Math.max(fontPt * .55, (heightPt - totalHeight) / 2 + fontPt * .55) + verticalOffsetPt;
  lines.forEach((line, index) => ctx.fillText(line, drawX, (startY + index * lineHeightPt) * scale, widthPt * scale));
  ctx.restore();
  ctx.textAlign = "left";
}

function drawCharactersAtPdfCenters(
  ctx: CanvasRenderingContext2D,
  value: string,
  centersPt: number[],
  topPt: number,
  fontPt: number,
  scale: number,
): void {
  const characters = value.replace(/\s+/g, "").slice(0, centersPt.length).split("");
  fontFor(ctx, fontPt * scale, 400);
  ctx.textAlign = "center";
  const y = (topPt + fontPt / 2) * scale;
  characters.forEach((character, index) => {
    ctx.fillText(character, centersPt[index] * scale, y);
  });
  ctx.textAlign = "left";
}

function drawStorageAddress(ctx: CanvasRenderingContext2D, value: string, scale: number): void {
  const compact = value.replace(/\s+/g, "").slice(0, 10);
  const centers = [306.90, 326.925, 347.875, 374.575, 395.60, 416.575, 443.20, 464.65, 491.35, 512.85];
  drawCharactersAtPdfCenters(ctx, compact, centers, 218.6018, 14, scale);
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob || blob.size < 500) { reject(new Error("Dokument konnte nicht vollständig gerendert werden.")); return; }
        resolve(blob);
      },
      type,
      quality,
    );
  });
}

function countInkPixels(pixels: Uint8ClampedArray): number {
  let count = 0;
  for (let index = 0; index < pixels.length; index += 4) {
    const luminance = (pixels[index] + pixels[index + 1] + pixels[index + 2]) / 3;
    if (luminance < 248) count += 1;
  }
  return count;
}

function renderFormOverlay(
  data: WithdrawalDocumentData,
  width: number,
  height: number,
): { canvas: HTMLCanvasElement; metrics: WithdrawalDocumentOverlayMetrics } {
  // Firefox hat in v0.117.0 auf einem vollständig transparent gestarteten
  // Canvas Text gezeichnet, dessen exportierte PNG-Alphaebene anschließend
  // komplett 0 war. Deshalb zeichnen wir die bewährte Feldlogik zuerst auf
  // einen OPaken weißen Scratch-Canvas. Erst danach wird aus der tatsächlichen
  // Helligkeit explizit eine Alpha-Maske erzeugt. Koordinaten, Schriftgrößen,
  // Wrap-/Shrink-Regeln und Zeichenmittelpunkte bleiben unverändert.
  const sourceCanvas = document.createElement("canvas");
  sourceCanvas.width = Math.max(1, Math.round(width));
  sourceCanvas.height = Math.max(1, Math.round(height));
  const ctx = sourceCanvas.getContext("2d", { alpha: false, willReadFrequently: true });
  if (!ctx) throw new Error("Materialentnahmeschein kann in diesem Browser nicht befüllt werden.");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, sourceCanvas.width, sourceCanvas.height);
  const scale = sourceCanvas.width / PAGE_WIDTH_PT;

  fitAndDrawPdfText(ctx, formatDate(data.withdrawalDate), scale, 84.38, 92.3584, 10, 62, 8, 400);
  fitAndDrawPdfBoxText(ctx, data.materialText, scale, 68, 125, 210, 35, 10, 7, 700);
  drawCharactersAtPdfCenters(
    ctx,
    data.materialNumber,
    [302.65, 326.35, 350.00, 373.70, 397.40, 421.00, 444.65, 468.35, 492.05, 515.70],
    137.9018,
    14,
    scale,
  );
  fitAndDrawPdfBoxText(ctx, data.materialType, scale, 65, 160, 213, 40, 10, 7, 700, "left", -2.25);
  fitAndDrawPdfBoxText(ctx, data.quantityRemoved, scale, 176, 204, 102, 34, 16, 11, 400, "center");
  fitAndDrawPdfBoxText(ctx, data.currentStock, scale, 176, 240, 102, 39, 16, 11, 400, "center", 2.25);
  drawStorageAddress(ctx, data.storageAddress, scale);
  fitAndDrawPdfBoxText(ctx, formatAllocationNumber(data.accountingNumber), scale, 365, 243, 166, 18, 14, 9, 400, "center");
  fitAndDrawPdfBoxText(ctx, data.accountingDescription, scale, 365, 263, 166, 16, 8, 6, 400, "center");
  fitAndDrawPdfBoxText(ctx, data.persons.join(", "), scale, 225, 291, 305, 30, 14, 8, 400, "center");
  fitAndDrawPdfBoxText(ctx, data.orderInfo, scale, 120, 329, 410, 20, 10, 6.5, 400);

  const dateProbeX = Math.max(0, Math.floor(70 * scale));
  const dateProbeY = Math.max(0, Math.floor(84 * scale));
  const dateProbeWidth = Math.min(sourceCanvas.width - dateProbeX, Math.ceil(82 * scale));
  const dateProbeHeight = Math.min(sourceCanvas.height - dateProbeY, Math.ceil(24 * scale));
  const dateProbe = ctx.getImageData(dateProbeX, dateProbeY, Math.max(1, dateProbeWidth), Math.max(1, dateProbeHeight));
  const dateProbeInkPixels = countInkPixels(dateProbe.data);

  const imageData = ctx.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
  let sourceInkPixels = 0;
  let alphaNonTransparentPixels = 0;
  let alphaMax = 0;
  for (let index = 0; index < imageData.data.length; index += 4) {
    const red = imageData.data[index];
    const green = imageData.data[index + 1];
    const blue = imageData.data[index + 2];
    const luminance = Math.round((red + green + blue) / 3);
    let alpha = 255 - luminance;
    if (alpha < 3) alpha = 0;
    if (alpha > 0) {
      sourceInkPixels += 1;
      alphaNonTransparentPixels += 1;
      if (alpha > alphaMax) alphaMax = alpha;
    }
    imageData.data[index] = 0;
    imageData.data[index + 1] = 0;
    imageData.data[index + 2] = 0;
    imageData.data[index + 3] = alpha;
  }

  const overlayCanvas = document.createElement("canvas");
  overlayCanvas.width = sourceCanvas.width;
  overlayCanvas.height = sourceCanvas.height;
  const overlayContext = overlayCanvas.getContext("2d", { alpha: true });
  if (!overlayContext) throw new Error("Materialentnahmeschein-Overlay kann in diesem Browser nicht erzeugt werden.");
  overlayContext.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
  overlayContext.putImageData(imageData, 0, 0);

  return {
    canvas: overlayCanvas,
    metrics: {
      width: overlayCanvas.width,
      height: overlayCanvas.height,
      scale,
      date_probe_ink_pixels: dateProbeInkPixels,
      source_ink_pixels: sourceInkPixels,
      alpha_nontransparent_pixels: alphaNonTransparentPixels,
      alpha_max: alphaMax,
    },
  };
}

async function buildCanonicalPdf(data: WithdrawalDocumentData, diagnostic?: WithdrawalDocumentDiagnostic): Promise<Blob> {
  const templateBytes = await loadTemplateBytes();
  if (diagnostic) diagnostic.template_bytes = templateBytes.byteLength;
  const pdfDocument = await PDFDocument.create();
  pdfDocument.setTitle("Materialentnahmeschein");
  pdfDocument.setCreator(`Materialentnahme PWA ${APP_VERSION}`);
  pdfDocument.setProducer(WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION);

  // Die Originalvorlage wird bytegenau direkt in die PDF eingebettet. Es gibt
  // keinen Browser-Bilddecoder und keinen Template-Canvas mehr im PDF-Pfad.
  const templateImage = await pdfDocument.embedPng(templateBytes);
  if (templateImage.width < 100 || templateImage.height < 100) {
    throw new Error("Materialentnahmeschein-Vorlage besitzt ungültige Abmessungen.");
  }

  const page = pdfDocument.addPage([PAGE_WIDTH_PT, PAGE_HEIGHT_PT]);
  page.drawImage(templateImage, { x: 0, y: 0, width: PAGE_WIDTH_PT, height: PAGE_HEIGHT_PT });

  // Die bisherige, bewährte Feldpositionierung wird als transparenter Overlay-
  // Canvas gerendert und exakt über dieselbe Seite gelegt.
  // Das Textlayout bleibt identisch in PDF-Punkten, benötigt für die finale
  // 180-dpi-Flattening-Ausgabe aber keinen 2480×1748-Zwischencanvas. Weniger
  // Pixel senken PNG-/Alpha-/PDF.js-Arbeit beim Draft-Save deutlich.
  const overlay = renderFormOverlay(data, PAGE_WIDTH_PT * FLATTEN_RENDER_SCALE, PAGE_HEIGHT_PT * FLATTEN_RENDER_SCALE);
  if (diagnostic) diagnostic.overlay = overlay.metrics;
  if (overlay.metrics.date_probe_ink_pixels < 20 || overlay.metrics.source_ink_pixels < 50 || overlay.metrics.alpha_max < 32) {
    throw new Error(`Materialentnahmeschein-Datenoverlay ist leer (Datum ${overlay.metrics.date_probe_ink_pixels} px · Overlay ${overlay.metrics.source_ink_pixels} px · Alpha ${overlay.metrics.alpha_max}).`);
  }
  const overlayBlob = await canvasToBlob(overlay.canvas, "image/png");
  overlay.metrics.overlay_png_bytes = overlayBlob.size;
  const overlayImage = await pdfDocument.embedPng(await overlayBlob.arrayBuffer());
  page.drawImage(overlayImage, { x: 0, y: 0, width: PAGE_WIDTH_PT, height: PAGE_HEIGHT_PT });

  const saved = await pdfDocument.save();
  if (saved.byteLength < 20_000) throw new Error("Materialentnahmeschein-PDF ist unerwartet klein und wurde verworfen.");
  if (diagnostic) diagnostic.pdf_bytes = saved.byteLength;
  const bytes = new Uint8Array(saved.byteLength);
  bytes.set(saved);
  return new Blob([bytes.buffer], { type: "application/pdf" });
}

async function renderPdfPageToOpaqueCanvas(pdfBlob: Blob, scale: number): Promise<HTMLCanvasElement> {
  let loadingTask: ReturnType<typeof getDocument> | null = null;
  try {
    const bytes = new Uint8Array(await pdfBlob.arrayBuffer());
    loadingTask = getDocument({ data: bytes });
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    const context = canvas.getContext("2d", { alpha: false, willReadFrequently: true });
    if (!context) throw new Error("Materialentnahmeschein kann in diesem Browser nicht final gerendert werden.");
    context.fillStyle = "#fff"; context.fillRect(0,0,canvas.width,canvas.height);
    await page.render({ canvas, canvasContext: context, viewport, background: "rgb(255,255,255)" }).promise;
    assertPreviewRendered(canvas);
    return canvas;
  } finally { if (loadingTask) { try { await loadingTask.destroy(); } catch { /* best effort */ } } }
}

async function flattenedPdfFromCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
  // Finale Archiv-/E-Mail-PDF enthält bewusst nur eine opake RGB-Seite. Keine
  // SMask/Transparenz mehr, damit Firefox PDF.js, Chrome/Edge und externe Reader
  // exakt denselben Inhalt anzeigen.
  const jpeg = await canvasToBlob(canvas, "image/jpeg", .97);
  const doc = await PDFDocument.create();
  doc.setTitle("Materialentnahmeschein");
  doc.setCreator(`Materialentnahme PWA ${APP_VERSION}`);
  doc.setProducer(WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION);
  const image = await doc.embedJpg(await jpeg.arrayBuffer());
  const page = doc.addPage([PAGE_WIDTH_PT,PAGE_HEIGHT_PT]);
  page.drawImage(image,{x:0,y:0,width:PAGE_WIDTH_PT,height:PAGE_HEIGHT_PT});
  const bytes = await doc.save({ useObjectStreams: false });
  const copy = new Uint8Array(bytes.byteLength); copy.set(bytes);
  return new Blob([copy.buffer],{type:"application/pdf"});
}

async function createFlattenedArtifacts(data: WithdrawalDocumentData, diagnostic?: WithdrawalDocumentDiagnostic): Promise<WithdrawalDocumentArtifacts> {
  const totalStarted = performance.now();
  const canonicalStarted = performance.now();
  const layered = await buildCanonicalPdf(data, diagnostic);
  const canonicalMs = performance.now() - canonicalStarted;

  const renderStarted = performance.now();
  const finalCanvas = await renderPdfPageToOpaqueCanvas(layered, FLATTEN_RENDER_SCALE);
  const renderMs = performance.now() - renderStarted;

  let pdfMs = 0;
  let webpMs = 0;
  const [pdf, preview] = await Promise.all([
    (async () => {
      const started = performance.now();
      const value = await flattenedPdfFromCanvas(finalCanvas);
      pdfMs = performance.now() - started;
      return value;
    })(),
    (async () => {
      const started = performance.now();
      const value = await canvasToBlob(finalCanvas, "image/webp", .9);
      webpMs = performance.now() - started;
      return value;
    })(),
  ]);
  if (diagnostic) {
    diagnostic.pdf_bytes = pdf.size;
    diagnostic.preview = { width: finalCanvas.width, height: finalCanvas.height, webp_bytes: preview.size };
    diagnostic.timings_ms = {
      canonical_pdf: Math.round(canonicalMs),
      pdfjs_flatten_render: Math.round(renderMs),
      flattened_pdf_encode: Math.round(pdfMs),
      webp_encode: Math.round(webpMs),
      total: Math.round(performance.now() - totalStarted),
    };
  }
  return {
    pdf,
    preview,
    telemetry: {
      canonicalPdfMs: Math.round(canonicalMs),
      pdfjsFlattenRenderMs: Math.round(renderMs),
      flattenedPdfEncodeMs: Math.round(pdfMs),
      webpEncodeMs: Math.round(webpMs),
      totalMs: Math.round(performance.now() - totalStarted),
      pdfBytes: pdf.size,
      previewBytes: preview.size,
    },
  };
}

function assertPreviewRendered(canvas: HTMLCanvasElement): void {
  const sample = document.createElement("canvas");
  sample.width = 80;
  sample.height = 56;
  const sampleContext = sample.getContext("2d", { willReadFrequently: true });
  if (!sampleContext) throw new Error("Dokumentprüfung wird von diesem Browser nicht unterstützt.");
  sampleContext.drawImage(canvas, 0, 0, sample.width, sample.height);
  const pixels = sampleContext.getImageData(0, 0, sample.width, sample.height).data;
  let nonWhite = 0;
  for (let index = 0; index < pixels.length; index += 4) {
    if (pixels[index + 3] > 220 && (pixels[index] < 235 || pixels[index + 1] < 235 || pixels[index + 2] < 235)) nonWhite += 1;
  }
  if (nonWhite < 25) throw new Error("Materialentnahmeschein-Vorschau wurde leer gerendert und verworfen.");
}

function startDocumentDiagnostic(
  data: WithdrawalDocumentData,
  mode: WithdrawalDocumentDiagnostic["mode"],
): WithdrawalDocumentDiagnostic {
  return {
    id: typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    started_at: new Date().toISOString(),
    finished_at: null,
    status: "running",
    mode,
    app_version: APP_VERSION,
    artifact_version: WITHDRAWAL_DOCUMENT_ARTIFACT_VERSION,
    user_agent: navigator.userAgent,
    form_values: {
      withdrawalDate: data.withdrawalDate,
      materialNumber: data.materialNumber,
      materialText: data.materialText,
      materialType: data.materialType,
      storageAddress: data.storageAddress,
      quantityRemoved: data.quantityRemoved,
      currentStock: data.currentStock,
      persons: [...data.persons],
      accountingNumber: data.accountingNumber,
      accountingDescription: data.accountingDescription,
      orderInfo: data.orderInfo,
    },
  };
}

function finishDocumentDiagnostic(
  diagnostic: WithdrawalDocumentDiagnostic,
  status: "ok" | "error",
  error?: unknown,
): void {
  diagnostic.status = status;
  diagnostic.finished_at = new Date().toISOString();
  if (status === "error") diagnostic.error = error instanceof Error ? error.message : String(error ?? "Unbekannter Fehler");
  saveWithdrawalDocumentDiagnostic(diagnostic);
  if (status === "error") console.error("[Materialentnahme][Dokument-Rendering]", diagnostic);
  else console.info("[Materialentnahme][Dokument-Rendering]", diagnostic);
}

export async function createWithdrawalPreviewFromPdf(pdfBlob: Blob, diagnostic?: WithdrawalDocumentDiagnostic): Promise<Blob> {
  try {
    const canvas = await renderPdfPageToOpaqueCanvas(pdfBlob, PREVIEW_RENDER_SCALE);
    const preview = await canvasToBlob(canvas, "image/webp", .9);
    if (diagnostic) diagnostic.preview = { width: canvas.width, height: canvas.height, webp_bytes: preview.size };
    return preview;
  } catch (error) {
    throw new Error("WebP-Vorschau des Materialentnahmescheins konnte nicht erzeugt werden.", { cause: error });
  }
}

export async function createWithdrawalPreview(data: WithdrawalDocumentData): Promise<Blob> {
  const diagnostic = startDocumentDiagnostic(data, "preview");
  try {
    const artifacts = await createFlattenedArtifacts(data, diagnostic);
    finishDocumentDiagnostic(diagnostic, "ok");
    return artifacts.preview;
  } catch (error) {
    finishDocumentDiagnostic(diagnostic, "error", error);
    throw error;
  }
}

export async function createWithdrawalPdf(data: WithdrawalDocumentData): Promise<Blob> {
  const diagnostic = startDocumentDiagnostic(data, "pdf");
  try {
    const artifacts = await createFlattenedArtifacts(data, diagnostic);
    finishDocumentDiagnostic(diagnostic, "ok");
    return artifacts.pdf;
  } catch (error) {
    finishDocumentDiagnostic(diagnostic, "error", error);
    throw error;
  }
}

export async function createWithdrawalDocumentArtifacts(data: WithdrawalDocumentData): Promise<WithdrawalDocumentArtifacts> {
  // Eine einzige kanonische PDF aus den Formularfeldern. Die WebP-Datei wird
  // anschließend aus GENAU dieser PDF gerendert und bleibt überall in der PWA
  // die sichtbare Darstellungsform. Kamera/OCR/Vision sind hier nicht beteiligt.
  const diagnostic = startDocumentDiagnostic(data, "artifacts");
  try {
    const artifacts = await createFlattenedArtifacts(data, diagnostic);
    finishDocumentDiagnostic(diagnostic, "ok");
    return artifacts;
  } catch (error) {
    finishDocumentDiagnostic(diagnostic, "error", error);
    throw error;
  }
}

export async function uploadWithdrawalArtifact(
  withdrawalId: number,
  fileType: "pdf" | "pdf_preview" | "label",
  blob: Blob,
  filename: string,
): Promise<void> {
  const form = new FormData();
  form.append("file", new File([blob], filename, { type: blob.type }));
  const response = await fetchWithTimeout(`/api/withdrawals/${withdrawalId}/files/${fileType}`, {
    method: "POST",
    credentials: "include",
    body: form,
  }, 30_000);
  if (!response.ok) {
    let detail = `${filename} konnte nicht gespeichert werden (${response.status}).`;
    try {
      const payload = await response.json() as { detail?: string };
      if (payload.detail) detail = `${filename}: ${payload.detail} (${response.status})`;
    } catch { /* fallback */ }
    throw new Error(detail);
  }
}
